import React, { useEffect, useRef, useState } from 'react';
import api, { mediaUrl } from '../lib/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, Send, X, Image as ImageIcon, Paperclip, FileText, FileArchive, Package, Loader2, Trash2, Edit, RotateCw, Eye, ExternalLink, Download, Pin, EyeOff, Copy } from 'lucide-react';

const ACCEPT_STR = 'image/*,video/mp4,video/quicktime,video/webm,.jpg,.jpeg,.png,.webp,.gif,.mp4,.mov,.webm,application/pdf,.pdf,application/zip,.zip,application/vnd.android.package-archive,.apk,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';

const KIND_ICON = { image: ImageIcon, video: Package, pdf: FileText, zip: FileArchive, apk: Package, doc: FileText, other: FileText };

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' +
      d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminBroadcasts() {
  const [items, setItems] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const token = localStorage.getItem('gtc_token');

  const load = () => api.get('/broadcasts').then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/broadcasts/${deleteTarget.id}`);
      toast.success('Eliminato');
      setDeleteTarget(null);
      load();
    } catch { toast.error('Errore'); }
  };

  const resend = async (b) => {
    try { await api.post(`/broadcasts/${b.id}/resend`); toast.success('Broadcast inviato di nuovo'); load(); }
    catch { toast.error('Errore'); }
  };

  const togglePin = async (b) => {
    try { await api.put(`/broadcasts/${b.id}`, { is_pinned: !b.is_pinned }); load(); }
    catch { toast.error('Errore'); }
  };
  const toggleHide = async (b) => {
    try { await api.put(`/broadcasts/${b.id}`, { is_hidden: !b.is_hidden }); load(); }
    catch { toast.error('Errore'); }
  };
  const publish = async (b) => {
    try { await api.put(`/broadcasts/${b.id}`, { status: 'sent' }); toast.success('Pubblicato'); load(); }
    catch { toast.error('Errore'); }
  };
  const duplicate = async (b) => {
    try { await api.post(`/broadcasts/${b.id}/duplicate`); toast.success('Duplicato come bozza'); load(); }
    catch { toast.error('Errore'); }
  };

  return (
    <div className="space-y-4">
      <button
        data-testid="admin-new-broadcast-btn"
        onClick={() => { setEditing(null); setShowEditor(true); }}
        className="btn-gold w-full py-3 flex items-center justify-center gap-2"
      >
        <Send className="w-4 h-4" /> Nuovo Broadcast
      </button>

      <div className="space-y-3">
        {items.length === 0 && <div className="text-center text-white/40 text-sm py-6">Nessun broadcast ancora.</div>}
        {items.map((b) => {
          const atts = b.attachments && b.attachments.length ? b.attachments : (b.attachment ? [b.attachment] : []);
          const att = atts[0];
          const AttIcon = att ? KIND_ICON[att.kind] || FileText : null;
          const statusColor = b.status === 'draft' ? 'text-yellow-400' : b.status === 'scheduled' ? 'text-[var(--gold)]' : 'text-[var(--success)]';
          const statusLabel = b.status === 'draft' ? 'Bozza' : b.status === 'scheduled' ? `Programmato · ${fmtDate(b.scheduled_at)}` : 'Pubblicata';
          return (
            <div key={b.id} className={`card-surface p-3 flex gap-3 ${b.is_hidden ? 'opacity-50' : ''}`} data-testid={`admin-broadcast-${b.id}`}>
              {att && att.kind === 'image' ? (
                <img src={mediaUrl(att.url)} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-[var(--gold-soft)] border border-[var(--gold)]/20 flex items-center justify-center flex-shrink-0">
                  {att ? <AttIcon className="w-5 h-5 text-[var(--gold)]" /> : <Megaphone className="w-5 h-5 text-[var(--gold)]" />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap text-[10px]">
                  <span className={`font-semibold uppercase tracking-widest text-[9px] ${statusColor}`}>{statusLabel}</span>
                  {b.is_pinned && <Pin className="w-3 h-3 text-[var(--gold)]" />}
                  {b.is_hidden && <EyeOff className="w-3 h-3 text-white/40" />}
                  <span className="text-white/40 flex items-center gap-1 ml-auto">
                    <Eye className="w-3 h-3" /> {b.views_count || 0}
                  </span>
                </div>
                {b.title && <div className="font-heading font-medium text-white text-sm truncate mt-0.5">{b.title}</div>}
                {b.message && <div className="text-xs text-white/70 line-clamp-2 mt-0.5">{b.message}</div>}
                {atts.length > 1 && (
                  <div className="text-[10px] text-white/40 mt-1">{atts.length} allegati</div>
                )}
                <div className="flex flex-wrap gap-1 mt-2">
                  <button data-testid={`bc-view-${b.id}`} onClick={() => setPreviewItem(b)} className="p-1.5 text-white/60 hover:text-[var(--gold)]" title="Anteprima"><Eye className="w-3.5 h-3.5" /></button>
                  <button data-testid={`bc-edit-${b.id}`} onClick={() => { setEditing(b); setShowEditor(true); }} className="p-1.5 text-white/60 hover:text-[var(--gold)]" title="Modifica"><Edit className="w-3.5 h-3.5" /></button>
                  <button data-testid={`bc-pin-${b.id}`} onClick={() => togglePin(b)} className={`p-1.5 ${b.is_pinned ? 'text-[var(--gold)]' : 'text-white/60'} hover:text-[var(--gold)]`} title="Fissa in alto"><Pin className="w-3.5 h-3.5" /></button>
                  <button data-testid={`bc-hide-${b.id}`} onClick={() => toggleHide(b)} className="p-1.5 text-white/60 hover:text-[var(--gold)]" title="Nascondi/Mostra">
                    {b.is_hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                  <button data-testid={`bc-dup-${b.id}`} onClick={() => duplicate(b)} className="p-1.5 text-white/60 hover:text-[var(--gold)]" title="Duplica"><Copy className="w-3.5 h-3.5" /></button>
                  {b.status === 'draft' && (
                    <button data-testid={`bc-publish-${b.id}`} onClick={() => publish(b)} className="p-1.5 text-[var(--success)] hover:text-green-300" title="Pubblica"><Send className="w-3.5 h-3.5" /></button>
                  )}
                  {b.status === 'sent' && (
                    <button data-testid={`bc-resend-${b.id}`} onClick={() => resend(b)} className="p-1.5 text-white/60 hover:text-[var(--gold)]" title="Reinvia"><RotateCw className="w-3.5 h-3.5" /></button>
                  )}
                  <button data-testid={`bc-delete-${b.id}`} onClick={() => setDeleteTarget(b)} className="p-1.5 text-white/60 hover:text-red-400" title="Elimina"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {showEditor && (
          <BroadcastEditor initial={editing} onClose={() => setShowEditor(false)} onSaved={() => { setShowEditor(false); load(); }} />
        )}
        {previewItem && (
          <BroadcastPreview item={previewItem} onClose={() => setPreviewItem(null)} />
        )}
        {deleteTarget && (
          <Overlay onClose={() => setDeleteTarget(null)}>
            <h3 className="font-heading text-lg">Elimina broadcast</h3>
            <p className="text-sm text-white/70">Eliminare definitivamente questo broadcast? L'operazione è irreversibile.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-3 rounded-full border border-[var(--border-subtle)] text-white/80 text-sm">Annulla</button>
              <button data-testid="bc-delete-confirm" onClick={doDelete} className="flex-1 py-3 rounded-full bg-red-500 text-white font-semibold text-sm">Elimina</button>
            </div>
          </Overlay>
        )}
      </AnimatePresence>
    </div>
  );
}

function BroadcastEditor({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [title, setTitle] = useState(initial?.title || '');
  const [message, setMessage] = useState(initial?.message || '');
  const [linkUrl, setLinkUrl] = useState(initial?.link_url || '');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('now'); // now | schedule | draft
  const [scheduledAt, setScheduledAt] = useState('');
  const [notify, setNotify] = useState(true);
  const filesRef = useRef(null);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    setFiles((prev) => [...prev, ...arr]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!isEdit && !title.trim() && !message.trim() && files.length === 0) {
      return toast.error('Aggiungi almeno testo, immagine o file');
    }
    if (mode === 'schedule' && !scheduledAt) return toast.error('Seleziona data/ora');
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/broadcasts/${initial.id}`, {
          title: title.trim() || null,
          message,
          link_url: linkUrl.trim() || null,
        });
        toast.success('Aggiornato');
      } else {
        const form = new FormData();
        form.append('title', title);
        form.append('message', message);
        form.append('link_url', linkUrl);
        form.append('send_notification', String(notify));
        if (mode === 'draft') form.append('save_as_draft', 'true');
        if (mode === 'schedule' && scheduledAt) {
          form.append('scheduled_at', new Date(scheduledAt).toISOString());
        }
        files.forEach((f) => form.append('files', f));
        await api.post('/broadcasts', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(mode === 'draft' ? 'Salvato come bozza' : mode === 'schedule' ? 'Broadcast programmato' : 'Pubblicato');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose} maxWidth="max-w-md">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-medium flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-[var(--gold)]" />
          {isEdit ? 'Modifica broadcast' : 'Nuovo broadcast'}
        </h3>
        <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-4 h-4" /></button>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Titolo (opzionale)</div>
        <input data-testid="bc-title-input" className="input-gold" placeholder="Es. Aggiornamento importante" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Messaggio</div>
        <textarea
          data-testid="bc-message-input"
          className="input-gold min-h-[140px] resize-y"
          placeholder="Scrivi il tuo messaggio…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Link cliccabile (opzionale)</div>
        <input data-testid="bc-link-input" className="input-gold" placeholder="https://…" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
      </div>

      {!isEdit && (
        <>
          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((f, idx) => {
                const isImg = f.type.startsWith('image/');
                const isVideo = f.type.startsWith('video/');
                return (
                  <div key={idx} className="rounded-2xl border border-[var(--gold)]/30 p-2 bg-[var(--bg-elevated)] flex items-center gap-3">
                    {isImg ? (
                      <img src={URL.createObjectURL(f)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-[var(--gold-soft)] flex items-center justify-center flex-shrink-0">
                        {isVideo ? <Package className="w-5 h-5 text-[var(--gold)]" /> : <Paperclip className="w-5 h-5 text-[var(--gold)]" />}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white truncate">{f.name}</div>
                      <div className="text-[10px] text-white/50">{formatSize(f.size)}</div>
                    </div>
                    <button onClick={() => removeFile(idx)} className="p-2 text-red-400/80 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                );
              })}
            </div>
          )}

          <button
            data-testid="bc-attach-files"
            onClick={() => filesRef.current?.click()}
            className="w-full py-3 rounded-2xl border border-dashed border-[var(--border-subtle)] hover:border-[var(--gold)]/50 text-sm text-white/70 flex items-center justify-center gap-2"
          >
            <Paperclip className="w-4 h-4 text-[var(--gold)]" /> Aggiungi allegati
          </button>
          <input ref={filesRef} type="file" multiple accept={ACCEPT_STR} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <p className="text-[10px] text-white/40">Supportati: JPG, PNG, WEBP, GIF, MP4, PDF, ZIP, APK, DOC(X), XLS(X), PPT(X), TXT. Max 500MB per file.</p>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-white/50">Pubblicazione</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: 'now', l: 'Pubblica' },
                { k: 'schedule', l: 'Programma' },
                { k: 'draft', l: 'Bozza' },
              ].map((o) => (
                <button
                  key={o.k}
                  data-testid={`bc-mode-${o.k}`}
                  onClick={() => setMode(o.k)}
                  className={`py-2 rounded-full text-xs font-medium transition-colors ${
                    mode === o.k ? 'bg-[var(--gold)] text-black' : 'bg-[var(--bg-surface)] text-white/70 border border-[var(--border-subtle)]'
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            {mode === 'schedule' && (
              <input
                type="datetime-local"
                data-testid="schedule-datetime-input"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                className="input-gold"
              />
            )}
            {mode !== 'draft' && (
              <label className="flex items-center gap-3 cursor-pointer pt-1">
                <input
                  data-testid="bc-notify"
                  type="checkbox"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  className="w-4 h-4 accent-[var(--gold)]"
                />
                <span className="text-xs text-white/80">Invia notifica Push agli utenti</span>
              </label>
            )}
          </div>
        </>
      )}

      {isEdit && (initial.attachment || (initial.attachments && initial.attachments.length)) && (
        <p className="text-[10px] text-white/40">Allegato attuale non modificabile. Elimina il broadcast e crea un nuovo se vuoi cambiare l'allegato.</p>
      )}

      <button data-testid="bc-submit" onClick={submit} disabled={saving} className="btn-gold w-full py-3 flex items-center justify-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> {isEdit ? 'Salva modifiche' : (mode === 'draft' ? 'Salva bozza' : mode === 'schedule' ? 'Programma' : 'Pubblica')}</>}
      </button>
    </Overlay>
  );
}

function BroadcastPreview({ item, onClose }) {
  const att = item.attachment;
  return (
    <Overlay onClose={onClose} maxWidth="max-w-md">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm text-[var(--gold)] flex items-center gap-2"><Megaphone className="w-4 h-4" /> Anteprima</h3>
        <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-4 h-4" /></button>
      </div>
      {att && att.kind === 'image' && (
        <img src={mediaUrl(att.url)} alt="" className="w-full max-h-72 object-cover rounded-xl" />
      )}
      {item.title && <div className="font-heading text-lg font-medium">{item.title}</div>}
      {item.message && <div className="text-sm text-white/85 whitespace-pre-wrap break-words">{item.message}</div>}
      {item.link_url && (
        <a href={item.link_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[var(--gold)] text-sm">
          <ExternalLink className="w-3.5 h-3.5" /> {item.link_url}
        </a>
      )}
      {att && att.kind !== 'image' && (
        <div className="text-xs text-white/60">
          Allegato: <span className="text-white">{att.filename}</span> ({formatSize(att.size)})
        </div>
      )}
      <div className="text-[10px] text-white/40">{fmtDate(item.created_at)}</div>
    </Overlay>
  );
}

function Overlay({ children, onClose, maxWidth = 'max-w-sm' }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 40, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
        className={`card-surface p-5 w-full ${maxWidth} space-y-4 my-8`}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
